<template>
  <div class="home detail-actions-page">
    <section>
        <div class="py-8 px-8">
            <div class="detail-page-header mb-4">
                <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
                  <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
                </button>
                <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                  <div class="d-flex align-items-center">
                    <h3 class="detail-page-title mb-0 me-2">{{contract.title}}</h3>
                    <span class="badge bg-primary">{{contract.contract_number}}</span>
                  </div>
                  <div class="detail-page-actions">
                    <button @click="printContract" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                      <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                      {{$t('view')}}
                    </button>
                    <button @click="editGeneral" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-outline-primary btn-xs detail-page-action--secondary">
                      <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                      {{$t('edit')}} {{$t('details')}}
                    </button>
                  </div>
                </div>
            </div>
        </div>
    </section>

    <section class="px-8">
        <ul class="nav nav-tabs detail-page-tabs mb-4">
            <li class="nav-item">
                <a class="nav-link" :class="{active: activeTab === 'details'}" href="#" @click.prevent="activeTab = 'details'">{{$t('details')}}</a>
            </li>
            <li class="nav-item">
                <a class="nav-link" :class="{active: activeTab === 'history'}" href="#" @click.prevent="activeTab = 'history'">{{$t('history')}}</a>
            </li>
            <li class="nav-item">
                <a class="nav-link" :class="{active: activeTab === 'attachments'}" href="#" @click.prevent="activeTab = 'attachments'">{{$t('attachments')}}</a>
            </li>
        </ul>

        <div class="bg-white rounded shadow p-4">
            <div v-if="loading" class="text-center">{{$t('loading')}}...</div>
            
            <!-- Details Tab -->
            <div v-if="!loading && activeTab === 'details'">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('type')}}</label>
                        <div>{{$t(contract.type)}}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('linked_entity')}}</label>
                        <div>
                            <span v-if="contract.type === 'employee' && contract.employee">{{contract.employee.name}}</span>
                            <span v-else-if="contract.type === 'client' && contract.client">{{contract.client.name}}</span>
                            <span v-else-if="contract.type === 'supplier' && contract.supplier">{{contract.supplier.name}}</span>
                            <span v-else>-</span>
                        </div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('status')}}</label>
                        <div>
                            <span class="badge" :class="{
                                'bg-secondary': contract.status === 'draft',
                                'bg-info': contract.status === 'sent' || contract.status === 'viewed',
                                'bg-success': contract.status === 'signed' || contract.status === 'active',
                                'bg-danger': contract.status === 'declined' || contract.status === 'expired' || contract.status === 'terminated',
                                'bg-warning text-dark': contract.status === 'pending'
                            }">{{$t(contract.status)}}</span>
                        </div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('amount')}}</label>
                        <div>{{formatMoney(contract.amount)}}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('start_date')}}</label>
                        <div>{{formatDate(contract.start_date)}}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('end_date')}}</label>
                        <div>{{formatDate(contract.end_date)}}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('auto_renew')}}</label>
                        <div>{{contract.auto_renew ? $t('yes') : $t('no')}}</div>
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="fw-bold">{{$t('billing_frequency')}}</label>
                        <div>{{contract.billing_frequency}}</div>
                    </div>
                </div>
            </div>



            <!-- History Tab -->
            <div v-if="!loading && activeTab === 'history'">
                <document-history :history="history" :loading="historyLoading" />
            </div>

            <!-- Attachments Tab -->
            <div v-if="!loading && activeTab === 'attachments'">
                <Attachment :entity_id="contract.id" entity_type="contract" />
            </div>
        </div>
    </section>

    <AddEditContractModal ref="addEditModal" :contractData="editingContract" @saved="onContractSaved" />

    <ContractTemplateModal 
        v-if="printModalIsOpen" 
        @modalClosed="printModalIsOpen = false" 
        modalId="print-contract-modal" 
        :isModalOpen="printModalIsOpen"  
        :data="contract"
    />
  </div>
</template>

<script>
import helper from '../../helper'
import Attachment from '../../components/Attachment.vue'
import AddEditContractModal from '../../components/contract/AddEditContractModal.vue'
import ContractTemplateModal from '../../components/contract/ContractTemplateModal.vue'
import DocumentHistory from '../../components/DocumentHistory.vue'

export default {
  name: 'ContractDetails',
  components: {
    Attachment,
    AddEditContractModal,
    ContractTemplateModal,
    DocumentHistory
  },
  data(){
    return {
        contract: {},
        loading: false,
        activeTab: 'details',
        history: [],
        historyLoading: false,
        printModalIsOpen: false,
        editingContract: null
    }
  },
  computed: {
    business() {
      return this.$store.getters.BUSINESS_DETAILS || { name: localStorage.getItem('businessName') || 'Company' };
    }
  },
  watch: {
    activeTab(val) {
        if (val === 'history') {
            this.getHistory();
        }
    }
  },
  mounted(){
    this.getContract()
  },
  methods: {
    formatMoney(number) { return helper.formatMoney(number);},
    formatDate(date) { return helper.formatDate(date) },
    formatDateTime(date) { return helper.formatDateTime(date) },
    async getContract(){
        try{
            this.loading = true
            const response = await helper.customGet(`${process.env.VUE_APP_API}/contracts/${this.$route.params.id}`)
            this.contract = response.data
            this.loading = false
        }catch(error){
            this.loading = false
            this.$swal.fire({
                icon: 'error',
                title: this.$t('error'),
                text: error.message || error
            })
        }
    },
    async getHistory() {
        try {
            this.historyLoading = true;
            const response = await helper.customGet(`${process.env.VUE_APP_API}/contracts/${this.contract.id}/history`);
            this.history = response.data;
            this.historyLoading = false;
        } catch (error) {
            this.historyLoading = false;
            this.$swal.fire({
                icon: 'error',
                title: this.$t('error'),
                text: error.message || error
            })
        }
    },
    editGeneral() {
        this.editingContract = { ...this.contract };
        this.$nextTick(() => {
            this.$refs.addEditModal.show('general');
        });
    },
    editContent() {
        this.editingContract = { ...this.contract };
        this.$nextTick(() => {
            this.$refs.addEditModal.show('content');
        });
    },
    async onContractSaved() {
        // Re-fetch full contract with associations (client, employee, supplier)
        await this.getContract();
        if (this.activeTab === 'history') {
            this.getHistory();
        }
    },
    printContract() {
        this.printModalIsOpen = true;
    }
  }
}
</script>
